// Global Instructions Rule Applied!
//
// Configuration → Lookup Lists services: the header list, per-header values
// (trashed included — the disable toggle), value CRUD (disable-only v1: no
// hard delete) and the whole-order reorder. Same fetch conventions as
// documentConfigService; admin-gated at the backend, and protected categories
// ('Pipeline Type') refuse writes with a readable message.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  LookupListDetailRow,
  LookupListHeaderRow,
  validateLookupListDetailRow,
  validateLookupListHeaderRow
} from '../../../state_manager/data_models/lookup_lists_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchLookupLists = async (
  accessToken: string | null
): Promise<LookupListHeaderRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LOOKUP_LISTS),
    { method: API_ROUTES.CONFIG.LOOKUP_LISTS.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as LookupListHeaderRow[]
  validateLookupListHeaderRow(rows, { isArray: true })
  return rows
}

export const fetchLookupListDetails = async (
  headerId: string,
  accessToken: string | null
): Promise<LookupListDetailRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LOOKUP_LIST_DETAILS, { headerId }),
    { method: API_ROUTES.CONFIG.LOOKUP_LIST_DETAILS.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as LookupListDetailRow[]
  validateLookupListDetailRow(rows, { isArray: true })
  return rows
}

export const createLookupDetail = async (
  headerId: string,
  fields: { label: string; value?: string | null; sort_order?: number | null },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LOOKUP_DETAIL_CREATE),
    {
      method: API_ROUTES.CONFIG.LOOKUP_DETAIL_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { header_id: headerId, ...fields } })
    }
  )
  await readJson(response)
}

export const updateLookupDetail = async (
  headerId: string,
  detailId: string,
  fields: { label?: string; sort_order?: number | null; trashed?: boolean },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LOOKUP_DETAIL_UPDATE, { detailId }),
    {
      method: API_ROUTES.CONFIG.LOOKUP_DETAIL_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { header_id: headerId, ...fields } })
    }
  )
  await readJson(response)
}

export const reorderLookupDetails = async (
  headerId: string,
  orderedIds: string[],
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.LOOKUP_DETAILS_REORDER),
    {
      method: API_ROUTES.CONFIG.LOOKUP_DETAILS_REORDER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { header_id: headerId, ordered_ids: orderedIds } })
    }
  )
  await readJson(response)
}
